import React, {useEffect} from "react";
import {useCsContext} from "../../state/CsInstance";
import {observer} from "mobx-react-lite";
import {faEllipsis} from "@fortawesome/sharp-solid-svg-icons/faEllipsis";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {serializeToJson} from "../../util/messengerUtils";

import TheaterCanvasButtons from "../TheaterCanvasButtons/TheaterCanvasButtons";
import styles from "./TheaterControls.module.scss";
import TheaterSettingsModal from "../TheaterModal/TheaterSettingsModal";
import {ErrorBoundary} from "react-error-boundary";
import ErrorComponent from "../ErrorComponent/ErrorComponent";

type TheaterControlsProps = {
	buttonClass?: string;
};

const TheaterControls: React.FC<TheaterControlsProps> = (props) => {
	const {
		theaterStore,
		userInputStore,
		uiStore,
		compileStore,
		multiplayerStore,
		getOrCreateMessenger,
	} = useCsContext();
	const {buttonClass} = props;

	// TODO useCallback these
	async function requestUpdateFromMessenger() {
		const inputJson = serializeToJson(
			1,
			userInputStore.mouseX,
			userInputStore.mouseY,
			userInputStore.isMouseDown,
			userInputStore.getKeysPressed()
		);
		const theaterState = await getOrCreateMessenger().updateTheater(inputJson, {
			forceUpdate: true,
		});
		if (theaterState) {
			multiplayerStore.setMyActorFrames(theaterState);
		}
	}

	function onClickPlayButton(
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) {
		userInputStore.isTheaterFocused = true;

		theaterStore.isRunning = !theaterStore.isRunning;
		if (theaterStore.isRunning) {
			getOrCreateMessenger().playTheater(); // for onPlay()
		} else {
			getOrCreateMessenger().pauseTheater(); // for onPause()
		}
		requestUpdateFromMessenger();
		event.currentTarget.blur(); // deselect
	}

	function onClickStepButton() {
		theaterStore.isRunning = false;
		getOrCreateMessenger().pauseTheater();
		requestUpdateFromMessenger();
	}

	function onClickResetButton() {
		theaterStore.clearLog();
		theaterStore.showConsole = false;

		getOrCreateMessenger().pauseTheater();
		getOrCreateMessenger().resetTheater();
		theaterStore.resetTheaterState();

		// Reset multiplayer state
		multiplayerStore.resetTrackedUuid();

		// TODO this is a hack to reset the console log, which should not live here
		compileStore.error = "";
		theaterStore.showConsole = false;
	}

	// When clicking outside the theater settings modal, close it.
	useEffect(() => {
		if (!uiStore.theaterSettingsOpen) return; // already closed
		function handleClick(e: MouseEvent) {
			if (e.target && uiStore.shouldCloseTheaterSettingsModal) {
				// close the modal unless a theater settings button was clicked
				uiStore.theaterSettingsOpen = false;
			}
			uiStore.shouldCloseTheaterSettingsModal = true; // reset the flag
		}

		// Defer the attachment of the document click listener to the next tick, after the current click (opening
		// the modal) is finished
		setTimeout(() => document.addEventListener("click", handleClick), 0);
		return () => document.removeEventListener("click", handleClick); // remove listener after closed
	}, [uiStore, uiStore.theaterSettingsOpen]);

	return (
		<ErrorBoundary FallbackComponent={ErrorComponent}>
			<div className={styles.theaterControlsContainer}>
				<TheaterCanvasButtons
					onClickRunButton={onClickPlayButton}
					onClickStepButton={onClickStepButton}
					onClickResetButton={onClickResetButton}
					buttonClass={buttonClass}
				/>
				<div className={styles.theaterSecondaryControlsContainer}>
					<div
						style={{
							position: "relative",
						}}
					>
						<button
							className={[styles.theaterSecondaryControlButton].join(" ")}
							onClick={() =>
								(uiStore.theaterSettingsOpen = !uiStore.theaterSettingsOpen)
							}
							aria-label={
								uiStore.theaterSettingsOpen
									? "Close Theater Settings"
									: "Open Theater Settings"
							}
						>
							<FontAwesomeIcon icon={faEllipsis} />
						</button>
						<TheaterSettingsModal />
					</div>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default observer(TheaterControls);
